import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { 
  Share, 
  PlusSquare, 
  MoreVertical, 
  Download, 
  Languages, 
  ChevronRight, 
  Sparkles,
  Smartphone,
  CheckCircle2
} from 'lucide-react';

interface PWAInstallModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  defaultPlatform?: 'ios' | 'android';
}

export const PWAInstallModal: React.FC<PWAInstallModalProps> = ({
  isOpen,
  onOpenChange,
  defaultPlatform = 'ios',
}) => {
  const [platform, setPlatform] = useState<'ios' | 'android'>(defaultPlatform);
  const [lang, setLang] = useState<'en' | 'es'>('en');

  // Sync defaultPlatform when opened
  React.useEffect(() => {
    if (isOpen) {
      setPlatform(defaultPlatform);
    }
  }, [isOpen, defaultPlatform]);

  const content = {
    en: {
      tag: "Mobile App (PWA)",
      title: "Install BASS Line on your Phone",
      subtitle: "Fast progressive web app. No App Store download or memory clutter required.",
      iosTab: "iPhone (Safari)",
      androidTab: "Android (Chrome)",
      langSwitch: "Español",
      closeBtn: "Got it, continue",
      iosSteps: [
        {
          number: "1",
          icon: <Share className="w-4 h-4 text-primary" />,
          title: "1. Tap the Share Button",
          badge: "⎋ Share",
          desc: "In Safari at the bottom bar, tap the Share icon (the square with an arrow pointing up)."
        },
        {
          number: "2",
          icon: <ChevronRight className="w-4 h-4 text-primary" />,
          title: "2. Scroll & Tap 'See More'",
          badge: "👁️ See More",
          desc: "Scroll down the sharing options and tap 'See More' or locate home screen actions."
        },
        {
          number: "3",
          icon: <PlusSquare className="w-4 h-4 text-primary" />,
          title: "3. Tap 'Add to Home Screen'",
          badge: "➕ Add to Home Screen",
          desc: "Select 'Add to Home Screen', then tap 'Add' in the top right. BASS Line will appear on your phone!"
        }
      ],
      androidSteps: [
        {
          number: "1",
          icon: <MoreVertical className="w-4 h-4 text-primary" />,
          title: "1. Open Chrome Menu",
          badge: "⋮ Menu",
          desc: "In Google Chrome, tap the 3 vertical dots (⋮) in the top right corner."
        },
        {
          number: "2",
          icon: <Download className="w-4 h-4 text-primary" />,
          title: "2. Tap 'Install App'",
          badge: "📲 Install app",
          desc: "Select 'Install app' or 'Add to Home screen' from the menu list."
        },
        {
          number: "3",
          icon: <CheckCircle2 className="w-4 h-4 text-primary" />,
          title: "3. Confirm & Launch",
          badge: "🚀 Confirm",
          desc: "Tap 'Install' in the prompt dialog. BASS Line will be added to your home screen and app drawer!"
        }
      ],
      footerNote: "Fast offline access • Push notifications • Zero app store updates needed"
    },
    es: {
      tag: "App Móvil (PWA)",
      title: "Instala BASS Line en tu Teléfono",
      subtitle: "Aplicación web rápida y ligera. Sin descargas en tiendas de apps ni consumo de memoria.",
      iosTab: "iPhone (Safari)",
      androidTab: "Android (Chrome)",
      langSwitch: "English",
      closeBtn: "¡Entendido, continuar!",
      iosSteps: [
        {
          number: "1",
          icon: <Share className="w-4 h-4 text-primary" />,
          title: "1. Toca el botón Compartir",
          badge: "⎋ Compartir",
          desc: "En la barra inferior de Safari, presiona el botón Compartir (el icono cuadrado con flecha hacia arriba)."
        },
        {
          number: "2",
          icon: <ChevronRight className="w-4 h-4 text-primary" />,
          title: "2. Desliza y toca 'Ver Más'",
          badge: "👁️ Ver Más",
          desc: "Desliza la lista de opciones hacia abajo y toca 'Ver Más' para ver todas las acciones del sistema."
        },
        {
          number: "3",
          icon: <PlusSquare className="w-4 h-4 text-primary" />,
          title: "3. Toca 'Agregar al Inicio'",
          badge: "➕ Agregar al Inicio",
          desc: "Selecciona 'Agregar al Inicio' (➕) y pulsa 'Agregar' arriba a la derecha. ¡BASS Line quedará lista!"
        }
      ],
      androidSteps: [
        {
          number: "1",
          icon: <MoreVertical className="w-4 h-4 text-primary" />,
          title: "1. Abre el Menú de Chrome",
          badge: "⋮ Menú",
          desc: "En Google Chrome, toca el menú de tres puntos verticales (⋮) en la esquina superior derecha."
        },
        {
          number: "2",
          icon: <Download className="w-4 h-4 text-primary" />,
          title: "2. Toca 'Instalar Aplicación'",
          badge: "📲 Instalar aplicación",
          desc: "Selecciona 'Instalar aplicación' o 'Agregar a la pantalla principal'."
        },
        {
          number: "3",
          icon: <CheckCircle2 className="w-4 h-4 text-primary" />,
          title: "3. Confirmar e Iniciar",
          badge: "🚀 Instalar",
          desc: "Presiona 'Instalar' en la ventana emergente. ¡BASS Line se añadirá a tu pantalla principal!"
        }
      ],
      footerNote: "Acceso offline instantáneo • Notificaciones push • Sin tiendas de apps"
    }
  };

  const t = content[lang];

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md w-[92vw] p-6 bg-card text-card-foreground border border-border shadow-2xl rounded-2xl">
        {/* Top Header */}
        <div className="flex items-center justify-between gap-2 pb-2">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-primary/10 text-primary border border-primary/20">
            <Smartphone className="w-3.5 h-3.5" />
            <span>{t.tag}</span>
          </div>

          {/* Language Switch Button */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setLang(lang === 'en' ? 'es' : 'en')}
            className="h-8 px-2.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted rounded-full transition-all gap-1.5"
          >
            <Languages className="w-3.5 h-3.5 text-primary" />
            <span>{t.langSwitch}</span>
          </Button>
        </div>

        <DialogHeader className="text-left space-y-1.5 pt-1">
          <DialogTitle className="text-xl font-bold tracking-tight text-foreground">
            {t.title}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground leading-relaxed">
            {t.subtitle}
          </DialogDescription>
        </DialogHeader>

        {/* Platform Tabs (iOS vs Android) */}
        <div className="space-y-4 pt-2">
          <Tabs 
            value={platform} 
            onValueChange={(val) => setPlatform(val as 'ios' | 'android')}
            className="w-full"
          >
            <TabsList className="grid grid-cols-2 w-full bg-muted p-1 border border-border/60 rounded-xl h-10">
              <TabsTrigger 
                value="ios"
                className="rounded-lg text-xs font-semibold transition-all data-[state=active]:bg-card data-[state=active]:text-primary data-[state=active]:shadow-sm flex items-center justify-center gap-2"
              >
                <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.37c.61-.74 1.04-1.79.92-2.87-1 .04-2.14.67-2.79 1.42-.56.64-1.05 1.7-0.92 2.74 1.12.09 2.19-.55 2.79-1.29z"/>
                </svg>
                <span>{t.iosTab}</span>
              </TabsTrigger>

              <TabsTrigger 
                value="android"
                className="rounded-lg text-xs font-semibold transition-all data-[state=active]:bg-card data-[state=active]:text-primary data-[state=active]:shadow-sm flex items-center justify-center gap-2"
              >
                <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                  <path d="M17.523 15.3414c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.5511 0 .9993.4482.9993.9993.0001.5511-.4482.9997-.9993.9997m-11.046 0c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.5511 0 .9993.4482.9993.9993 0 .5511-.4482.9997-.9993.9997m11.4045-6.02l1.9973-3.4592a.416.416 0 00-.1521-.5676.416.416 0 00-.5676.1521l-2.0223 3.503C15.5902 8.411 13.8562 8.084 12 8.084c-1.8562 0-3.5902.327-5.1368.8657L4.8409 5.4467a.4161.4161 0 00-.5677-.1521.4157.4157 0 00-.1521.5676l1.9973 3.4592C2.6889 11.1867.3432 14.6589 0 18.761h24c-.3432-4.1021-2.6889-7.5743-6.1185-9.4396"/>
                </svg>
                <span>{t.androidTab}</span>
              </TabsTrigger>
            </TabsList>

            {/* iOS Steps */}
            <TabsContent value="ios" className="space-y-2.5 mt-3 focus-visible:outline-none">
              {t.iosSteps.map((step, idx) => (
                <div 
                  key={idx}
                  className="flex items-start gap-3 p-3 rounded-xl bg-muted/40 border border-border/70 hover:border-primary/30 transition-all group"
                >
                  <div className="w-7 h-7 shrink-0 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center font-bold text-primary text-xs">
                    {step.number}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <h4 className="text-xs font-semibold text-foreground group-hover:text-primary transition-colors">
                        {step.title}
                      </h4>
                      <span className="text-[10px] font-mono tracking-wide px-1.5 py-0.5 rounded bg-background text-muted-foreground border border-border shrink-0">
                        {step.badge}
                      </span>
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      {step.desc}
                    </p>
                  </div>
                </div>
              ))}
            </TabsContent>

            {/* Android Steps */}
            <TabsContent value="android" className="space-y-2.5 mt-3 focus-visible:outline-none">
              {t.androidSteps.map((step, idx) => (
                <div 
                  key={idx}
                  className="flex items-start gap-3 p-3 rounded-xl bg-muted/40 border border-border/70 hover:border-primary/30 transition-all group"
                >
                  <div className="w-7 h-7 shrink-0 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center font-bold text-primary text-xs">
                    {step.number}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <h4 className="text-xs font-semibold text-foreground group-hover:text-primary transition-colors">
                        {step.title}
                      </h4>
                      <span className="text-[10px] font-mono tracking-wide px-1.5 py-0.5 rounded bg-background text-muted-foreground border border-border shrink-0">
                        {step.badge}
                      </span>
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      {step.desc}
                    </p>
                  </div>
                </div>
              ))}
            </TabsContent>
          </Tabs>

          <div className="text-center pt-1">
            <p className="text-[11px] text-muted-foreground flex items-center justify-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              {t.footerNote}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="pt-2">
          <Button
            type="button"
            onClick={() => onOpenChange(false)}
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-medium text-xs h-10 rounded-xl transition-all shadow-sm"
          >
            {t.closeBtn}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PWAInstallModal;
